import type { BattleRes, SupCat } from './constants.ts';
import { MILLI } from './quantity.ts';
import { haveOf, measured, prepState, type LotLike } from './rules.ts';
import type { ItemUnit, ShipAvailability, ShipState, ShipStatus, ShortLine } from './schemas.ts';

// Sierra's fleet rules as pure functions (plan 2B R8–R10), ported from
// sierra-panel.html at the lines given. tests/fleet-rules.test.ts holds the
// plan's tables. The server and the pages both use them, so the board, the badges
// and the live status agree.

export interface FleetItem {
  id: string;
  name: string;
  category: SupCat;
  unit: ItemUnit;
  battleResource: BattleRes | null;
}

export interface FleetShip {
  id: string;
  stocked: boolean;
  availability: ShipAvailability;
}

export interface FleetLevel {
  item: string;
  target: number;
}

/** Down: a claim pending or unavailable, whatever's aboard. */
export function isDown(availability: ShipAvailability): boolean {
  return availability === 'Unavailable' || availability === 'Claim pending';
}

/**
 * Cargo used (line 1358): the crates' SCU, plus materials, plus SCU items, in
 * thousandths. Kits and counted items aren't counted (Open Question 4).
 */
export function cargoUsed(items: ReadonlyMap<string, FleetItem>, lots: readonly LotLike[]): number {
  return lots.reduce((sum, lot) => {
    const item = items.get(lot.item);
    if (item === undefined || (item.unit !== 'crates' && item.unit !== 'scu')) return sum;
    return sum + measured(item.unit, lot);
  }, 0);
}

/**
 * R8 (`shipStatus`, line 1356). The state goes registry, then down, then no prep
 * levels, then short, then ready. `lots` and `levels` are the ship's own: the lots
 * aboard, and its active prep levels.
 */
export function shipStatus(ship: FleetShip, items: ReadonlyMap<string, FleetItem>, lots: readonly LotLike[], levels: readonly FleetLevel[]): ShipStatus {
  const place = { kind: 'ship' as const, shipId: ship.id };
  const set = levels.filter((level) => level.target > 0 && items.has(level.item));
  const short: ShortLine[] = [];
  for (const level of set) {
    const item = items.get(level.item);
    if (item === undefined) continue;
    const have = haveOf(item, place, lots);
    if (prepState('ship', have, level.target) === 'low') short.push({ item: level.item, have, target: level.target });
  }
  const state: ShipState = !ship.stocked ? 'registry' : isDown(ship.availability) ? 'down' : set.length === 0 ? 'noprep' : short.length > 0 ? 'short' : 'ready';
  return {
    ship: ship.id,
    state,
    prepLevels: set.length,
    short,
    pct: !ship.stocked || set.length === 0 ? null : Math.round(((set.length - short.length) / set.length) * 100),
    cargoUsed: cargoUsed(items, lots),
  };
}

/** The board's counts (lines 1417–1420). */
export interface BoardCounts {
  /** Stocked ships that are ready. */
  ready: number;
  /** Stocked ships that need a restock. */
  short: number;
  /** Stocked ships with no prep levels. */
  noprep: number;
  /** All ships claim pending or unavailable. */
  down: number;
  stocked: number;
  /** All ships. */
  registry: number;
  /** Member ships not stocked. */
  notStocked: number;
}

export function boardCounts(ships: readonly FleetShip[], statuses: ReadonlyMap<string, ShipStatus>): BoardCounts {
  const stocked = ships.filter((ship) => ship.stocked);
  const count = (state: ShipState) => stocked.filter((ship) => statuses.get(ship.id)?.state === state).length;
  return {
    ready: count('ready'),
    short: count('short'),
    noprep: count('noprep'),
    down: ships.filter((ship) => isDown(ship.availability)).length,
    stocked: stocked.length,
    registry: ships.length,
    notStocked: ships.length - stocked.length,
  };
}

export interface OwnedShip {
  name: string;
  /** The owner's name as shown: a member's display name, or a guest's name. '' with no owner. */
  ownerName: string;
  /** The owner's user id, or ''. */
  ownerId: string;
}

/** 0 for the pinned owner, 1 for other owners, 2 for no owner. The pin matches a user id or a name, ignoring case. */
export function ownerRank(ship: OwnedShip, pinned: string): number {
  const pin = pinned.trim().toLowerCase();
  if (pin !== '' && (ship.ownerId.toLowerCase() === pin || ship.ownerName.trim().toLowerCase() === pin)) return 0;
  return ship.ownerName.trim() === '' ? 2 : 1;
}

/** R9 (`byOwner`, line 1353): the pinned owner first, then other owners by name, then no owner; within an owner, by name. */
export function byOwner(pinned: string): (a: OwnedShip, b: OwnedShip) => number {
  // Owners compare ignoring case, as the board groups them (line 1424).
  return (a, b) => ownerRank(a, pinned) - ownerRank(b, pinned) || a.ownerName.localeCompare(b.ownerName, 'en', { sensitivity: 'base' }) || a.name.localeCompare(b.name);
}

export interface Family<S> {
  ship: S;
  carried: S[];
}

/**
 * Ships grouped under the ships that carry them (one level deep), for the board.
 * A family is shown when any of its ships matches (line 1395). A ship whose
 * carrier isn't in the list is shown on its own.
 */
export function families<S extends { id: string; carriedBy: string | null }>(ships: readonly S[], matches: (ship: S) => boolean = () => true): Family<S>[] {
  const ids = new Set(ships.map((ship) => ship.id));
  const carried = new Map<string, S[]>();
  for (const ship of ships) {
    if (ship.carriedBy !== null && ship.carriedBy !== ship.id && ids.has(ship.carriedBy)) carried.set(ship.carriedBy, [...(carried.get(ship.carriedBy) ?? []), ship]);
  }
  return ships
    .filter((ship) => ship.carriedBy === null || ship.carriedBy === ship.id || !ids.has(ship.carriedBy))
    .map((ship) => ({ ship, carried: carried.get(ship.id) ?? [] }))
    .filter((family) => [family.ship, ...family.carried].some(matches));
}

/** One battle resource aboard: SCU in thousandths, and the variance, the largest crate's size in thousandths. */
export interface ResourceAboard {
  amount: number;
  variance: number;
}

/**
 * R10 (`shipRes`, line 494): the battle resources aboard a ship. Crates count as
 * full, so the variance is the largest crate, the most a part-used one can be off
 * by. Every battle resource is in SCU, so counted items and kits aren't counted.
 */
export function shipResources(items: ReadonlyMap<string, FleetItem>, lots: readonly LotLike[]): Partial<Record<BattleRes, ResourceAboard>> {
  const aboard: Partial<Record<BattleRes, ResourceAboard>> = {};
  for (const lot of lots) {
    const item = items.get(lot.item);
    if (item === undefined || item.battleResource === null || lot.amount <= 0) continue;
    if (item.unit !== 'crates' && item.unit !== 'scu') continue;
    const entry = (aboard[item.battleResource] ??= { amount: 0, variance: 0 });
    entry.amount += measured(item.unit, lot);
    if (item.unit === 'crates') entry.variance = Math.max(entry.variance, (lot.crateSize ?? 0) * MILLI);
  }
  return aboard;
}
